<template>
  <Modal :show="show" title="Manage User Permissions" size="max-w-4xl" @close="$emit('close')">
    <div v-if="permissionStore.loading" class="flex justify-center items-center h-32">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
    </div>

    <div v-else>
      <div class="mb-6">
        <h4 class="text-lg font-medium text-gray-900 mb-2">User: {{ user?.firstName }} {{ user?.lastName }}</h4>
        <p class="text-sm text-gray-600">{{ user?.emailAddress?.String }}</p>
      </div>

      <p class="text-sm text-gray-500 mb-4">
        <i class="fa-solid fa-info-circle mr-1"></i>
        Drag permissions between lists to assign or remove them
      </p>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <h5 class="text-md font-medium text-gray-900 mb-3">
            <i class="fa-solid fa-list text-gray-500 mr-2"></i>
            Available Permissions
          </h5>
          <div
            class="min-h-48 max-h-64 overflow-y-auto border-2 border-dashed border-gray-300 rounded-lg p-3 transition-colors"
            :class="{ 'border-red-400 bg-red-50': isDraggingOver === 'available' }"
            @dragover.prevent="handleDragOver('available')"
            @dragleave="handleDragLeave"
            @drop="handleDrop('available', $event)"
          >
            <div v-if="availablePermissions.length === 0" class="flex items-center justify-center h-full text-sm text-gray-400 italic">
              All permissions assigned
            </div>
            <div v-else class="space-y-2">
              <div
                v-for="permission in availablePermissions"
                :key="permission.id"
                draggable="true"
                @dragstart="handleDragStart(permission, 'available', $event)"
                @dragend="handleDragEnd"
                class="flex items-center p-3 bg-white border border-gray-200 rounded-md cursor-grab hover:shadow-md hover:border-green-300 transition-all"
                :class="{ 'opacity-50': draggingPermission?.id === permission.id }"
              >
                <div class="flex-shrink-0 mr-3 text-gray-400">
                  <i class="fa-solid fa-grip-vertical"></i>
                </div>
                <div class="flex-1 min-w-0">
                  <span class="text-sm font-medium text-gray-900 block truncate">{{ permission.permission }}</span>
                </div>
                <button
                  @click="assignPermission(permission.id)"
                  :disabled="permissionStore.loading"
                  class="ml-2 p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded transition-colors"
                  title="Assign permission"
                >
                  <i class="fa-solid fa-chevron-right"></i>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Assigned Permissions -->
        <div>
          <h5 class="text-md font-medium text-gray-900 mb-3">
            <i class="fa-solid fa-check-circle text-green-500 mr-2"></i>
            Assigned Permissions
          </h5>
          <div
            class="min-h-48 max-h-64 overflow-y-auto border-2 border-dashed border-gray-300 rounded-lg p-3 transition-colors"
            :class="{ 'border-green-400 bg-green-50': isDraggingOver === 'assigned' }"
            @dragover.prevent="handleDragOver('assigned')"
            @dragleave="handleDragLeave"
            @drop="handleDrop('assigned', $event)"
          >
            <div v-if="permissionStore.userPermissions.length === 0" class="flex items-center justify-center h-full text-sm text-gray-400 italic">
              Drop permissions here to assign
            </div>
            <div v-else class="space-y-2">
              <div
                v-for="permission in permissionStore.userPermissions"
                :key="permission.id"
                draggable="true"
                @dragstart="handleDragStart(permission, 'assigned', $event)"
                @dragend="handleDragEnd"
                class="flex items-center p-3 bg-green-50 border border-green-200 rounded-md cursor-grab hover:shadow-md hover:border-green-400 transition-all"
                :class="{ 'opacity-50': draggingPermission?.id === permission.id }"
              >
                <button
                  @click="removePermission(permission.id)"
                  :disabled="permissionStore.loading"
                  class="mr-2 p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded transition-colors"
                  title="Remove permission"
                >
                  <i class="fa-solid fa-chevron-left"></i>
                </button>
                <div class="flex-1 min-w-0">
                  <span class="text-sm font-medium text-gray-900 block truncate">{{ permission.permission }}</span>
                </div>
                <div class="flex-shrink-0 ml-3 text-gray-400">
                  <i class="fa-solid fa-grip-vertical"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <button
        @click="$emit('close')"
        class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
      >
        Close
      </button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue';
import { usePermissionStore } from '@/store/permissions';
import { useNotificationStore } from '@/store/notifications';
import Modal from './modal.vue';

interface Permission {
  id: string;
  name: string;
  description: string;
}

interface User {
  id: string;
  firstName: string;
  lastName: string;
  emailAddress: { String: string };
}

const props = defineProps<{
  show: boolean;
  user: User;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'permissionsUpdated'): void;
}>();

const permissionStore = usePermissionStore();
const notificationStore = useNotificationStore();

const draggingPermission = ref<Permission | null>(null);
const draggingFrom = ref<'available' | 'assigned' | null>(null);
const isDraggingOver = ref<'available' | 'assigned' | null>(null);

const availablePermissions = computed(() =>
  permissionStore.permissions.filter(
    (perm) => !permissionStore.userPermissions.some((userPerm) => userPerm.id === perm.id)
  )
);

function handleDragStart(permission: Permission, from: 'available' | 'assigned', event: DragEvent) {
  draggingPermission.value = permission;
  draggingFrom.value = from;
  if (event.dataTransfer) {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', permission.id);
  }
}

function handleDragEnd() {
  draggingPermission.value = null;
  draggingFrom.value = null;
  isDraggingOver.value = null;
}

function handleDragOver(target: 'available' | 'assigned') {
  if (draggingFrom.value && draggingFrom.value !== target) {
    isDraggingOver.value = target;
  }
}

function handleDragLeave() {
  isDraggingOver.value = null;
}

async function assignPermission(permissionId: string) {
  try {
    const purpose = localStorage.getItem('purpose');
    if (purpose === 'public') {
      await permissionStore.assignPermission(props.user.id, permissionId);
    } else if (purpose === 'local') {
      await permissionStore.assignAdminPermission(props.user.id, permissionId);
    }
    if (permissionStore.error) {
      notificationStore.showError('Permission Assignment Failed', permissionStore.error);
    } else {
      notificationStore.showSuccess('Permission Assigned', 'Permission has been assigned successfully.');
      emit('permissionsUpdated');
    }
  } catch (err) {
    notificationStore.showError('Assignment Error', 'An unexpected error occurred while assigning permission.');
  }
}

async function removePermission(permissionId: string) {
  try {
    const purpose = localStorage.getItem('purpose');
    if (purpose === 'public') {
      await permissionStore.removePermission(props.user.id, permissionId);
    } else if (purpose === 'local') {
      await permissionStore.removeAdminPermission(props.user.id, permissionId);
    }
    if (permissionStore.error) {
      notificationStore.showError('Permission Removal Failed', permissionStore.error);
    } else {
      notificationStore.showSuccess('Permission Removed', 'Permission has been removed successfully.');
      emit('permissionsUpdated');
    }
  } catch (err) {
    notificationStore.showError('Removal Error', 'An unexpected error occurred while removing permission.');
  }
}

async function handleDrop(target: 'available' | 'assigned', event: DragEvent) {
  event.preventDefault();
  isDraggingOver.value = null;
  const purpose = localStorage.getItem('purpose');

  if (!draggingPermission.value || draggingFrom.value === target) return;

  if (target === 'assigned') {
    await assignPermission(draggingPermission.value.id);
  } else {
    await removePermission(draggingPermission.value.id);
  }

  handleDragEnd();
}

watch(() => props.show, async (newShow) => {
  if (newShow && props.user) {
    const purpose = localStorage.getItem('purpose');
    if (purpose === 'public') {
      await permissionStore.fetchPermissions();
      await permissionStore.fetchUserPermissions(props.user.id);
    } else if (purpose === 'local') {
      await permissionStore.fetchAdminPermissions();
      await permissionStore.fetchAdminUserPermissions(props.user.id);
    }
  }
});

onMounted(async () => {
  if (props.show && props.user) {
    const purpose = localStorage.getItem('purpose');
    if (purpose === 'public') {
      await permissionStore.fetchPermissions();
      await permissionStore.fetchUserPermissions(props.user.id);
    } else if (purpose === 'local') {
      await permissionStore.fetchAdminPermissions();
      await permissionStore.fetchAdminUserPermissions(props.user.id);
    }
  }
});
</script>
